import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { ANNOUNCEMENT_STATUS_FILTER_LABELS, hasPermission } from '@blimo/shared';
import { Button, DataTable, ErrorState, PageHeader, SearchInput } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../../components/filter-bar.jsx';
import { PropertyFilter } from '../../finance/panels.jsx';
import { useListState } from '../../../lib/list-search.js';
import { AnnouncementStateBadge, PublishWindow } from '../common.jsx';

const STATUS_OPTIONS = Object.entries(ANNOUNCEMENT_STATUS_FILTER_LABELS).map(([value, label]) => ({ value, label }));

/** Kommunikation › Mitteilungen (F-501, F-502). */
export function AnnouncementsListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = { ...list.apiParams, sort: undefined, filter: { search: search.q, status: search.status, property_id: search.property } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.announcements(api, params));
  const columns = useMemo(() => announcementColumns(), []);

  return (
    <>
      <PageHeader
        title="Mitteilungen"
        description="Mitteilungen an Eigentümer je Liegenschaft – Veröffentlichung im Portal und Versand per E-Mail."
        actions={
          hasPermission(me, 'announcements.manage') && (
            <Button asChild size="sm">
              <Link to="/kommunikation/mitteilungen/neu">
                <Plus aria-hidden="true" /> Mitteilung erstellen
              </Link>
            </Button>
          )
        }
      />
      <FilterBar>
        <SearchInput label="Mitteilungen durchsuchen" placeholder="Titel …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Mitteilungen"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Mitteilungen gefunden"
        />
      )}
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Spalten werden im Liegenschafts-Tab wiederverwendet
export function announcementColumns() {
  return [
    {
      id: 'title',
      header: 'Titel',
      cell: ({ row }) => (
        <span className="flex flex-col">
          <Link
            to="/kommunikation/mitteilungen/$announcementId"
            params={{ announcementId: String(row.original.id) }}
            className="font-medium text-primary hover:underline"
          >
            {row.original.title}
          </Link>
          {row.original.teaser && <span className="line-clamp-1 text-xs text-muted-foreground">{row.original.teaser}</span>}
        </span>
      ),
    },
    { id: 'state', header: 'Status', cell: ({ row }) => <AnnouncementStateBadge announcement={row.original} /> },
    { id: 'window', header: 'Zeitraum', cell: ({ row }) => <PublishWindow from={row.original.publish_at} to={row.original.unpublish_at} /> },
    {
      id: 'properties',
      header: 'Liegenschaften',
      cell: ({ row }) => {
        const props = row.original.properties ?? [];
        if (props.length === 0) return <span className="text-muted-foreground">–</span>;
        return (
          <span className="text-sm">
            {props
              .slice(0, 2)
              .map((p) => p.name)
              .join(', ')}
            {props.length > 2 && <span className="text-muted-foreground"> +{props.length - 2}</span>}
          </span>
        );
      },
    },
    {
      id: 'dispatches',
      header: 'Versand',
      cell: ({ row }) => {
        const count = row.original.dispatches?.length ?? 0;
        return count === 0 ? <span className="text-muted-foreground">nicht versendet</span> : <span>{count}× versendet/eingereiht</span>;
      },
    },
  ];
}
